/**
 * Bundle Builder - Custom element for Build Your Bundle page
 * Handles: Add to Bundle / Quantity, Effect filters, Side cart
 */
class BundleBuilder extends HTMLElement {
  get maxProducts() {
    return parseInt(this.getAttribute('data-max-products'), 10) || 5;
  }
  constructor() {
    super();
    this.bundleItems = [];
  }

  connectedCallback() {
    this.initEventDelegation();
    this.initEffectFilters();
    this.syncSideCart();
  }

  getTotalBundleCount() {
    let total = 0;
    this.querySelectorAll('[data-bundle-card]').forEach((card) => {
      const qtyState = card.querySelector('[data-bundle-qty-state]');
      if (!qtyState || qtyState.classList.contains('d-none')) return;
      const qtyInput = card.querySelector('[data-bundle-qty-input]');
      if (!qtyInput) return;
      total += parseInt(qtyInput.value, 10) || 0;
    });
    return total;
  }

  getBundleItems() {
    const items = [];
    this.querySelectorAll('[data-bundle-card]').forEach((card) => {
      const qtyState = card.querySelector('[data-bundle-qty-state]');
      if (!qtyState || qtyState.classList.contains('d-none')) return;
      const qtyInput = card.querySelector('[data-bundle-qty-input]');
      if (!qtyInput) return;
      const qty = parseInt(qtyInput.value, 10) || 1;
      if (qty < 1) return;
      const variantId = card.querySelector('[data-bundle-add-btn]')?.getAttribute('data-variant-id');
      if (!variantId) return;
      const title = card.getAttribute('data-product-title') || '';
      const image = card.getAttribute('data-product-image') || '';
      const price = parseInt(card.getAttribute('data-product-price'), 10) || 0;
      let sellingPlans = [];
      const spScript = card.querySelector('script[data-bundle-selling-plans]');
      try { sellingPlans = JSON.parse(spScript?.textContent || '[]'); } catch (_) { /* skip */ }
      for (let i = 0; i < qty; i++) {
        items.push({ variantId, quantity: 1, title, image, price, sellingPlans });
      }
    });
    return items;
  }

  syncSideCart() {
    const items = this.getBundleItems();
    const totalCount = this.getTotalBundleCount();
    const bundleFull = totalCount >= this.maxProducts;
    const sideCart = document.querySelector('bundle-side-cart');
    if (sideCart) {
      sideCart.setItems(items);
    }

    this.querySelectorAll('[data-bundle-card]').forEach((card) => {
      const qtyState = card.querySelector('[data-bundle-qty-state]');
      const isInBundle = qtyState && !qtyState.classList.contains('d-none');
      const addBtn = card.querySelector('[data-bundle-add-btn]');
      const selectedBadge = card.querySelector('[data-bundle-selected-badge]');

      if (selectedBadge) {
        selectedBadge.classList.toggle('d-none', !isInBundle);
      }
      if (addBtn) {
        const productUnavailable = addBtn.getAttribute('data-product-available') === 'false';
        const shouldDisable = !isInBundle && bundleFull;
        addBtn.disabled = productUnavailable || shouldDisable;
        addBtn.setAttribute('aria-disabled', (productUnavailable || shouldDisable) ? 'true' : 'false');
      }
    });

    this.querySelectorAll('[data-bundle-qty-increase]').forEach((el) => {
      const card = el.closest('[data-bundle-card]');
      const qtyState = card?.querySelector('[data-bundle-qty-state]');
      const isVisible = qtyState && !qtyState.classList.contains('d-none');
      const input = card?.querySelector('[data-bundle-qty-input]');
      const currentQty = input ? parseInt(input.value, 10) || 1 : 0;
      const productMax = parseInt(input?.getAttribute('max'), 10) || this.maxProducts;
      const canAddMore = isVisible && totalCount < this.maxProducts && currentQty < productMax;
      const shouldDisable = !canAddMore;
      el.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      el.classList.toggle('bundle-btn-disabled', shouldDisable);
      el.setAttribute('tabindex', shouldDisable ? '-1' : '0');
    });

    this.querySelectorAll('[data-bundle-qty-decrease]').forEach((el) => {
      const card = el.closest('[data-bundle-card]');
      const qtyState = card?.querySelector('[data-bundle-qty-state]');
      const isVisible = qtyState && !qtyState.classList.contains('d-none');
      const input = card?.querySelector('[data-bundle-qty-input]');
      const currentQty = input ? parseInt(input.value, 10) || 1 : 0;
      const shouldDisable = !isVisible || currentQty <= 1;
      el.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      el.classList.toggle('bundle-btn-disabled', shouldDisable);
      el.setAttribute('tabindex', shouldDisable ? '-1' : '0');
    });
  }

  initEventDelegation() {
    this.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-bundle-add-btn]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleAddToBundleClick(e);
        return;
      }
      const qtyIncrease = e.target.closest('[data-bundle-qty-increase]');
      if (qtyIncrease) {
        e.preventDefault();
        this.handleQtyChange(e, 1);
        return;
      }
      const qtyDecrease = e.target.closest('[data-bundle-qty-decrease]');
      if (qtyDecrease) {
        e.preventDefault();
        this.handleQtyChange(e, -1);
        return;
      }
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-bundle-qty-input]')) {
        this.handleQtyInputChange(e);
      }
    });
  }

  handleAddToBundleClick(event) {
    const btn = event.target.closest('[data-bundle-add-btn]');
    const card = btn?.closest('[data-bundle-card]');
    if (!card) return;
    if (this.getTotalBundleCount() >= this.maxProducts) return;

    const addState = card.querySelector('[data-bundle-add-state]');
    const qtyState = card.querySelector('[data-bundle-qty-state]');
    const qtyInput = card.querySelector('[data-bundle-qty-input]');

    if (addState && qtyState && qtyInput) {
      addState.classList.add('d-none');
      qtyState.classList.remove('d-none');
      qtyInput.value = '1';
      qtyInput.focus();
      this.syncSideCart();
    }
  }

  handleQtyChange(event, delta) {
    const card = event.target.closest('[data-bundle-card]');
    const input = card?.querySelector('[data-bundle-qty-input]');
    if (!input) return;

    const productMax = parseInt(input.getAttribute('max'), 10) || this.maxProducts;
    const totalCount = this.getTotalBundleCount();
    const currentQty = parseInt(input.value, 10) || 1;
    const maxForProduct = Math.min(productMax, this.maxProducts - totalCount + currentQty);

    let val = currentQty + delta;
    if (delta > 0 && val > maxForProduct) return;
    val = Math.max(0, Math.min(val, maxForProduct));

    const hint = card?.querySelector('[data-bundle-limited-hint]');
    if (val === 0) {
      const addState = card.querySelector('[data-bundle-add-state]');
      const qtyState = card.querySelector('[data-bundle-qty-state]');
      if (addState && qtyState) {
        qtyState.classList.add('d-none');
        addState.classList.remove('d-none');
      }
      input.value = '1';
      if (hint) {
        hint.classList.add('d-none');
        hint.setAttribute('aria-hidden', 'true');
      }
    } else {
      input.value = String(val);
      if (hint) {
        const productMax = parseInt(input.getAttribute('max'), 10) || this.maxProducts;
        if (val >= productMax) {
          hint.classList.remove('d-none');
          hint.setAttribute('aria-hidden', 'false');
          hint.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          hint.classList.add('d-none');
          hint.setAttribute('aria-hidden', 'true');
        }
      }
    }
    this.syncSideCart();
  }

  handleQtyInputChange(event) {
    const input = event.currentTarget;
    const max = parseInt(input.getAttribute('max'), 10) || this.maxProducts;
    let val = parseInt(input.value, 10) || 1;
    val = Math.max(1, Math.min(val, max));
    input.value = String(val);
    this.syncSideCart();
  }

  initEffectFilters() {
    const filterBar = this.querySelector('[data-effect-filters]');
    const groups = this.querySelectorAll('[data-bundle-group]');
    if (!filterBar || !groups.length) return;

    filterBar.querySelectorAll('[data-product-filter]').forEach((li) => {
      const btn = li.querySelector('[data-effect-filter-btn]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const filterValue = li.getAttribute('data-product-filter') || 'all';
        filterBar.querySelectorAll('[data-product-filter]').forEach((f) => f.classList.remove('active'));
        li.classList.add('active');

        groups.forEach((group) => {
          const groupKey = group.getAttribute('data-bundle-group');
          const show = filterValue === 'all' || groupKey === filterValue;
          group.classList.toggle('d-none', !show);
        });

        requestAnimationFrame(() => {
          const firstVisibleGroup = Array.from(groups).find((group) => !group.classList.contains('d-none'));
          if (firstVisibleGroup) {
            const rect = firstVisibleGroup.getBoundingClientRect();
            const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight;
            
            if (!isInViewport) {
              const elementTop = rect.top + window.pageYOffset;
              window.scrollTo({ top: elementTop - 180, behavior: 'smooth' });
            }
          }
        });
      });
    });
  }

}

class BundleSideCart extends HTMLElement {
  constructor() {
    super();
    this.items = [];
  }

  connectedCallback() {
    this.discountTiers = this.loadDiscountTiers();
    this.populateFrequencyDropdown();
    this.render();
    this.querySelector('[data-bundle-add-to-cart]')?.addEventListener('click', (e) => this.handleAddToCart(e));
    this.initSubscriptionToggle();
    this.syncFooterToHeadingVars();
    this.initHeadingToggle();
  }

  initHeadingToggle() {
    const heading = this.querySelector('[data-bundle-side-cart-heading]');
    if (!heading) return;
    heading.setAttribute('tabindex', '0');
    heading.setAttribute('role', 'button');
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-bundle-side-cart-heading]')) {
        document.body.classList.toggle('bundle-active');
      }
    });
    heading.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        document.body.classList.toggle('bundle-active');
      }
    });
  }

  loadDiscountTiers() {
    const script = this.closest('.col-12, .col-lg-4')
      ?.querySelector('script[data-bundle-discount-tiers]')
      || document.querySelector('script[data-bundle-discount-tiers]');
    try {
      const raw = JSON.parse(script?.textContent || '{}');
      return Object.entries(raw)
        .map(([qty, data]) => ({
          qty: parseInt(qty, 10),
          amount: parseInt(data.amount, 10),
          variant_id: parseInt(data.variant_id, 10) || 0,
          selling_plans: data.selling_plans || [],
        }))
        .sort((a, b) => a.qty - b.qty);
    } catch (_) { return []; }
  }

  getActiveTier(qty) {
    let active = null;
    for (const tier of this.discountTiers) {
      if (qty >= tier.qty) active = tier;
      else break;
    }
    return active;
  }

  getTierDiscount(qty) {
    let discount = 0;
    for (const tier of this.discountTiers) {
      if (qty >= tier.qty) discount = tier.amount;
      else break;
    }
    return discount;
  }

  getNextTier(qty) {
    for (const tier of this.discountTiers) {
      if (qty < tier.qty) return tier;
    }
    return null;
  }

  populateFrequencyDropdown() {
    const select = this.querySelector('[data-bundle-frequency-select]');
    if (!select) return;

    const plans = this.discountTiers[0]?.selling_plans || [];
    if (!plans.length) return;

    select.innerHTML = '';
    plans.forEach((plan, i) => {
      const opt = document.createElement('option');
      opt.value = plan.id;
      opt.textContent = plan.name;
      opt.dataset.discount = plan.discount || 0;
      if (i === 0) opt.selected = true;
      select.appendChild(opt);
    });

    const firstDiscount = plans[0]?.discount || 0;
    const pctEl = this.querySelector('[data-bundle-subscription-percentage]');
    if (pctEl) pctEl.textContent = `${Math.floor(firstDiscount)}%`;

    select.addEventListener('change', (e) => {
      const selectedOption = e.target.options[e.target.selectedIndex];
      const discount = parseFloat(selectedOption?.dataset.discount || 0);
      const pctEl = this.querySelector('[data-bundle-subscription-percentage]');
      if (pctEl) pctEl.textContent = `${Math.floor(discount)}%`;

      this.updateSubscriptionPrices();
    });
  }

  initSubscriptionToggle() {
    const widget = this.querySelector('[data-bundle-subscription-widget]');
    if (!widget) return;

    widget.addEventListener('change', (e) => {
      const radio = e.target.closest('[data-bundle-purchase-radio]');
      if (!radio) return;

      widget.querySelectorAll('.widget-options').forEach((opt) => opt.classList.remove('selected'));
      const parentOption = radio.closest('.widget-options');
      if (parentOption) parentOption.classList.add('selected');
      
      this.updateCheckoutTotal();
    });
  }

  updateSubscriptionPrices() {
    const totalPrice = this.items.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
    const tierDiscount = this.getTierDiscount(this.items.length);

    const discountPct = parseInt(this.querySelector('[data-bundle-subscription-percentage]')?.textContent, 10) || 15;
    const subscriptionPrice = Math.round(totalPrice * (1 - discountPct / 100)) - tierDiscount;

    const subPriceEl = this.querySelector('[data-bundle-subscription-price]');
    if (subPriceEl) subPriceEl.textContent = this.formatMoney(Math.max(0, subscriptionPrice));

    this.updateCheckoutTotal();
  }

  updateCheckoutTotal() {
    const checkoutTotal = this.querySelector('[data-checkout-total]');
    if (!checkoutTotal) return;
    
    if (this.items.length === 0) {
      checkoutTotal.textContent = '';
      return;
    }

    const totalPrice = this.items.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
    const tierDiscount = this.getTierDiscount(this.items.length);
    const discountedOnetimePrice = Math.max(0, totalPrice - tierDiscount);

    const widget = this.querySelector('[data-bundle-subscription-widget]');
    const selectedRadio = widget?.querySelector('[data-bundle-purchase-radio]:checked');
    const purchaseType = selectedRadio?.value || 'onetime';

    if (purchaseType === 'subscription') {
      const discountPct = parseInt(this.querySelector('[data-bundle-subscription-percentage]')?.textContent, 10) || 15;
      const subscriptionPrice = Math.round(totalPrice * (1 - discountPct / 100)) - tierDiscount;
      checkoutTotal.textContent = ` - ${this.formatMoney(Math.max(0, subscriptionPrice))}`;
    } else {
      checkoutTotal.textContent = ` - ${this.formatMoney(discountedOnetimePrice)}`;
    }
  }

  syncFooterToHeadingVars() {
    const heading = this.querySelector('[data-bundle-side-cart-heading]');
    const footer = this.querySelector('[data-bundle-side-cart-footer]');
    if (!heading || !footer) return;

    const apply = () => {
      this.style.setProperty('--bundle-heading-height', `${heading.offsetHeight}px`);
      this.style.setProperty('--bundle-footer-height', `${footer.offsetHeight}px`);
    };

    apply();
    if (typeof ResizeObserver !== 'undefined') {
      this._footerHeadingRO = new ResizeObserver(apply);
      this._footerHeadingRO.observe(heading);
      this._footerHeadingRO.observe(footer);
    }
  }

  setItems(items) {
    this.items = items;
    this.render();
  }

  render() {
    const container = this.querySelector('[data-bundle-side-cart-items]');
    if (!container) return;

    const totalPrice = this.items.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0);
    const totalQty = this.items.length;

    const maxProducts = parseInt(this.getAttribute('data-max-products'), 10) || 5;
    const addProductText = this.getAttribute('data-add-product-text') || 'Add';

    const tierDiscount = this.getTierDiscount(totalQty);
    const discountedOnetimePrice = Math.max(0, totalPrice - tierDiscount);

    const headingLabel = this.querySelector('[data-bundle-side-cart-heading-label]');
    const headingCount = this.querySelector('[data-bundle-side-cart-heading-count]');
    if (headingLabel) {
      if (totalQty === 0) {
        headingLabel.textContent = 'Your Bundle';
      } else if (tierDiscount === 0) {
        headingLabel.textContent = 'Add More to Unlock Savings';
      } else {
        headingLabel.innerHTML = `<span class="emoji-icon">😊</span> ${this.formatMoney(tierDiscount)} Savings Unlocked!`;
      }
    }
    if (headingCount) {
      headingCount.textContent = `(${totalQty}/${maxProducts})`;
    }

    const banner = this.querySelector('[data-bundle-savings-banner]');
    if (banner) {
      if (tierDiscount > 0) {
        banner.innerHTML = `<div class="savings-active">😊 ${this.formatMoney(tierDiscount)} Savings Unlocked!</div>`;
        banner.classList.remove('d-none');
      } else {
        banner.innerHTML = '';
        banner.classList.add('d-none');
      }
    }

    const removeSvg = '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10.5 0.5L0.5 10.5M0.500001 0.5L10.5 10.5" stroke="#283D39" stroke-linecap="round"/></svg>';

    let html = '';
    this.items.forEach((item, idx) => {
      html += `
        <div class="bundle-item-card border">
          <button type="button" class="btn btn-link text-muted bundle-item-remove" data-bundle-side-cart-remove="${idx}" aria-label="Remove ${this.escapeHtml(item.title)}">
            ${removeSvg}
          </button>
          <div class="d-flex align-items-center gap-lg-1 gap-2 imgs">
            <img src="${this.escapeHtml(item.image)}" alt="" width="50" height="50" class="rounded object-fit-cover flex-shrink-0" loading="lazy">
            <div class="flex-grow-1 p-2 pe-lg-5 pe-4 ps-0">
              <p class="fs-sm fw-semibold text-truncate">${this.escapeHtml(item.title)}</p>
              <p class="mb-0 fs-xs prices" style="color: #4D4D4D;">${this.formatMoney(item.price)}</p>
            </div>
          </div>
        </div>
      `;
    });
    const tierMap = {};
    for (const tier of this.discountTiers) {
      tierMap[tier.qty] = tier.amount;
    }

    for (let i = totalQty; i < maxProducts; i++) {
      const slotQty = i + 1;
      const slotLabel = (slotQty === 1 && totalQty === 0) ? 'Start building your bundle' : `${addProductText} ${slotQty}`;
      let hintHtml = '';
      if (tierMap[slotQty]) {
        hintHtml = `<span class="tier-hint">Unlock<span class="fw-bold"> ${this.formatMoney(tierMap[slotQty])} discount</span></span>`;
      }
      html += `<div class="bundle-item-empty-card p-2 border rounded-1 text-muted text-center" data-bundle-item-empty-card="${slotQty}"><span class="titles">${slotLabel}</span>${hintHtml}</div>`;
    }
    container.innerHTML = html;

    const totalEl = this.querySelector('[data-bundle-side-cart-total]');
    if (totalEl) {
      if (tierDiscount > 0) {
        totalEl.innerHTML = `<s class="text-muted fw-normal fs-sm" style="color: #4D4D4D;">${this.formatMoney(totalPrice)}</s> ${this.formatMoney(discountedOnetimePrice)}`;
      } else {
        totalEl.textContent = this.formatMoney(totalPrice);
      }
    }

    const discountPct = parseInt(this.querySelector('[data-bundle-subscription-percentage]')?.textContent, 10) || 15;
    const subscriptionPrice = Math.round(totalPrice * (1 - discountPct / 100)) - tierDiscount;

    const subPriceEl = this.querySelector('[data-bundle-subscription-price]');
    if (subPriceEl) subPriceEl.textContent = this.formatMoney(Math.max(0, subscriptionPrice));

    const compareEl = this.querySelector('[data-bundle-onetime-compare-price]');
    if (compareEl) compareEl.textContent = this.formatMoney(totalPrice);

    this.updateCheckoutTotal();

    const canCheckout = this.items.length >= 2;
    const subscriptionWrapper = this.querySelector('[data-bundle-subscription-wrapper]');
    if (subscriptionWrapper) {
      subscriptionWrapper.classList.toggle('d-none', !canCheckout);
    }

    const addBtn = this.querySelector('[data-bundle-add-to-cart]');
    if (addBtn) {
      addBtn.disabled = !canCheckout;
      addBtn.classList.toggle('disabled', !canCheckout);
    }

    this.querySelectorAll('[data-bundle-side-cart-remove]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt(e.currentTarget.getAttribute('data-bundle-side-cart-remove'), 10);
        this.removeItemByCard(idx);
      });
    });

    if (typeof this.syncFooterToHeadingVars === 'function') {
      requestAnimationFrame(() => this.syncFooterToHeadingVars());
    }
  }

  removeItemByCard(itemIndex) {
    const builder = document.querySelector('bundle-builder');
    if (!builder) return;
    const item = this.items[itemIndex];
    if (!item) return;

    const card = Array.from(builder.querySelectorAll('[data-bundle-card]')).find((c) => {
      const qtyState = c.querySelector('[data-bundle-qty-state]');
      if (!qtyState || qtyState.classList.contains('d-none')) return false;
      const vid = c.querySelector('[data-bundle-add-btn]')?.getAttribute('data-variant-id');
      return vid === String(item.variantId);
    });
    if (!card) return;

    const qtyInput = card.querySelector('[data-bundle-qty-input]');
    const addState = card.querySelector('[data-bundle-add-state]');
    const qtyState = card.querySelector('[data-bundle-qty-state]');
    const hint = card.querySelector('[data-bundle-limited-hint]');
    if (!qtyInput || !addState || !qtyState) return;

    const currentQty = parseInt(qtyInput.value, 10) || 1;
    const newQty = currentQty - 1;

    if (newQty <= 0) {
      qtyState.classList.add('d-none');
      addState.classList.remove('d-none');
      qtyInput.value = '1';
      if (hint) {
        hint.classList.add('d-none');
        hint.setAttribute('aria-hidden', 'true');
      }
    } else {
      qtyInput.value = String(newQty);
      if (hint) {
        const productMax = parseInt(qtyInput.getAttribute('max'), 10) || 5;
        hint.classList.toggle('d-none', newQty < productMax);
        hint.setAttribute('aria-hidden', newQty < productMax ? 'true' : 'false');
      }
    }
    builder.syncSideCart();
  }

  generateBundleId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  getBundleProperties() {
    const widget = this.querySelector('[data-bundle-subscription-widget]');
    const purchaseType = widget?.querySelector('[data-bundle-purchase-radio]:checked')?.value || 'onetime';
    if (purchaseType !== 'subscription') return { purchaseType, parentSellingPlanId: null, selectedPlanName: null };

    const select = widget?.querySelector('[data-bundle-frequency-select]');
    const selectedOption = select?.options[select.selectedIndex];
    const parentSellingPlanId = selectedOption?.value || null;
    const selectedPlanName = selectedOption?.textContent?.trim() || null;

    return { purchaseType, parentSellingPlanId, selectedPlanName };
  }

  async handleAddToCart(event) {
    const btn = event.currentTarget;
    if (this.items.length < 2) return;

    const addTextEl = btn.querySelector('.add-text');
    const originalText = addTextEl?.textContent;
    btn.disabled = true;
    if (addTextEl) addTextEl.textContent = btn.getAttribute('data-adding-text') || 'Adding...';

    const bundleId = this.generateBundleId();
    const { purchaseType, selectedPlanName } = this.getBundleProperties();
    const activeTier = this.getActiveTier(this.items.length);
    const parentVariantId = activeTier?.variant_id || 0;

    const parentItem = {
      id: parentVariantId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_parent: true,
      },
    };
    let parentSellingPlan = null;
    if (purchaseType === 'subscription' && selectedPlanName && activeTier) {
      const matchedPlan = activeTier.selling_plans.find((p) => p.name === selectedPlanName);
      if (matchedPlan) {
        parentSellingPlan = matchedPlan.id;
        parentItem.selling_plan = parentSellingPlan;
      }
    }

    const combined = {};
    this.items.forEach((i) => {
      const id = parseInt(i.variantId, 10);
      if (!combined[id]) {
        combined[id] = { quantity: 0 };
      }
      combined[id].quantity += (i.quantity || 1);
    });

    const childItems = Object.entries(combined).map(([id, data]) => {
      const item = {
        id: parseInt(id, 10),
        quantity: data.quantity,
        parent_id: parentVariantId,
        properties: {
          _bundle_id: bundleId,
        },
      };

      if (purchaseType === 'subscription' && parentSellingPlan) {
        item.selling_plan = parentSellingPlan;
      }

      return item;
    });

    const items = [parentItem, ...childItems];

    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      await fetch(`${rootUrl}cart/clear.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });

      const cartUrl = `${rootUrl}cart/add.js`;
      const res = await fetch(cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();

      if (res.ok) {
        this.items = [];
        this.render();
        const builder = document.querySelector('bundle-builder');
        if (builder) {
          builder.querySelectorAll('[data-bundle-qty-state]').forEach((qs) => qs.classList.add('d-none'));
          builder.querySelectorAll('[data-bundle-add-state]').forEach((as) => as.classList.remove('d-none'));
          builder.querySelectorAll('[data-bundle-qty-input]').forEach((inp) => { inp.value = '1'; });
        }
        if (typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        } else {
          window.location.href = '/checkout';
        }
      } else {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }
    } catch (err) {
      console.error(err);
      if (addTextEl) addTextEl.textContent = btn.getAttribute('data-error-text') || 'Try again';
      setTimeout(() => {
        if (addTextEl) addTextEl.textContent = originalText || 'Add to Cart';
        btn.disabled = false;
      }, 2000);
      return;
    }
    btn.disabled = false;
    if (addTextEl) addTextEl.textContent = originalText || 'Add to Cart';
  }

  escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  formatMoney(cents) {
    if (typeof Utility !== 'undefined' && Utility.formatMoney && window.globalVariables?.money_format) {
      return Utility.formatMoney(cents, window.globalVariables.money_format);
    }
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }
}

customElements.define('bundle-builder', BundleBuilder);
customElements.define('bundle-side-cart', BundleSideCart);
